import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Pressable, View, ViewStyle } from 'react-native'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { parseISO } from 'date-fns'
import { debounce } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { InfoPopoverTriggerAction } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Display } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/display/Display'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { SignatureIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SignatureIcon'
import { InfoPopover } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/infoPopover/InfoPopover'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { DisplaySize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'

import { LibraryDocumentItem } from '../DocumentsPage'

const HeaderCellContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const FileNameContainer = styled.View(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  flexDirection: 'row',
  overflow: 'hidden',
  marginRight: theme.spacing['16px'],
}))

const SignatureIconWrapper = styled.View({
  flexShrink: 0,
  marginLeft: 6,
  alignSelf: 'center',
})

const FileNameStart = styled(BodyText)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flexShrink: 1,
  minWidth: 0,
})

const FileNameEnd = styled(BodyText)({
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

const FileNameCell: React.FC<{
  fileName: string
  documentId: string
  isRowHovered?: boolean
  signingFlow?: string
}> = ({ fileName, documentId, isRowHovered, signingFlow }) => {
  const intl = useIntl()
  const { colors } = useTheme()
  const [isTruncated, setIsTruncated] = useState(false)
  const elementId = `lib-file-name-${documentId}`
  const endChars = 10
  const showMiddleTruncation = fileName.length > endChars * 2

  useEffect(() => {
    const checkTruncation = () => {
      const el = document.getElementById(elementId)
      if (el) setIsTruncated(el.scrollWidth > el.clientWidth)
    }
    const debouncedCheck = debounce(checkTruncation, 100)
    requestAnimationFrame(checkTruncation)
    window.addEventListener('resize', debouncedCheck)
    return () => {
      window.removeEventListener('resize', debouncedCheck)
      debouncedCheck.cancel()
    }
  }, [fileName, elementId])

  const textStyle = isRowHovered ? { textDecorationLine: 'underline' as const } : undefined

  const signatureIcon = signingFlow ? (
    <SignatureIconWrapper>
      <Tooltip
        content={intl.formatMessage({
          defaultMessage: 'Signature required',
          description: 'Tooltip for signature required icon',
        })}
        hoverEnabled
        placement='top'
      >
        <SignatureIcon width={16} height={16} fillColor={colors.iconDefault} />
      </Tooltip>
    </SignatureIconWrapper>
  ) : null

  const content = (
    <FileNameContainer>
      {showMiddleTruncation ? (
        <>
          <FileNameStart id={elementId} style={textStyle}>
            {fileName.slice(0, -endChars)}
          </FileNameStart>
          <FileNameEnd style={textStyle}>{fileName.slice(-endChars)}</FileNameEnd>
        </>
      ) : (
        <FileNameStart id={elementId} style={textStyle}>
          {fileName}
        </FileNameStart>
      )}
      {signatureIcon}
    </FileNameContainer>
  )

  if (isTruncated) {
    return (
      <Tooltip
        content={fileName}
        hoverEnabled
        placement='top'
        wrapperStyle={{ flex: 1, minWidth: 0, display: 'flex' }}
        style={{ flex: 1, minWidth: 0, display: 'block' } as unknown as ViewStyle}
      >
        {content}
      </Tooltip>
    )
  }

  return content
}

const useDocumentsColumns = (
  getProgramLabel: (id: string) => string,
  isDaProvider: boolean,
): ColumnDef<LibraryDocumentItem, any>[] => {
  const intl = useIntl()
  const { colors } = useTheme()

  const formatDate = useCallback(
    (dateStr: string) => {
      try {
        return intl.formatDate(parseISO(dateStr), { year: 'numeric', month: '2-digit', day: '2-digit' })
      } catch {
        return dateStr
      }
    },
    [intl],
  )

  return useMemo(() => {
    const columnHelper = createColumnHelper<LibraryDocumentItem>()

    const baseColumns: ColumnDef<LibraryDocumentItem, any>[] = [
      columnHelper.accessor('fileName', {
        header: 'File Name',
        enableSorting: true,
        size: undefined,
        meta: { inlineComponent: true },
        cell: (ctx) => (
          <FileNameCell
            fileName={ctx.getValue()}
            documentId={ctx.row.original.id}
            isRowHovered={(ctx as { isRowHovered?: boolean }).isRowHovered}
            signingFlow={ctx.row.original.signingFlow}
          />
        ),
      }),
      columnHelper.accessor('type', {
        header: 'Type',
        enableSorting: true,
        size: 160,
      }),
      columnHelper.accessor('uploadedDate', {
        header: 'Uploaded Date',
        enableSorting: true,
        size: 160,
        cell: (ctx) => <BodyText text={formatDate(ctx.getValue() as string)} />,
      }),
    ]

    if (!isDaProvider) return baseColumns

    return [
      ...baseColumns,
      columnHelper.accessor('autoSend', {
        id: 'autoSend',
        enableSorting: false,
        size: 280,
        header: () => (
          <HeaderCellContainer>
            <Display size={DisplaySize.EYEBROW}>Auto Send</Display>
            <InfoPopover
              placement='bottom'
              header={
                <Title
                  size={TitleSize.XSMALL}
                  text={intl.formatMessage({
                    defaultMessage: 'What is auto-send?',
                    description: 'Title for the auto send info popover',
                  })}
                />
              }
              content={
                <BodyText
                  color={colors.textSecondary}
                  text={
                    <FormattedMessage
                      defaultMessage='Select documents to be automatically sent to new clients after they book an intake session.'
                      description='Body text for the auto send info popover'
                    />
                  }
                />
              }
              footerContent={
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                  <PrimaryButton
                    text={
                      <FormattedMessage
                        defaultMessage='Learn more'
                        description='Learn more button in the auto send info popover'
                      />
                    }
                    onPress={() =>
                      window.open(
                        'https://provider-support.lyrahealth.com/hc/en-us/articles/38528605874963--HOLD-New-CC-self-service-guide',
                        '_blank',
                      )
                    }
                  />
                </View>
              }
              allowContentUnderCloseButton
              popoverStyle={{ container: { width: '392px', boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.12)' } }}
              trigger={
                <Pressable accessibilityRole='button'>
                  <InfoIcon size={24} fillColor={colors.iconDefault} />
                </Pressable>
              }
              popoverTriggerAction={InfoPopoverTriggerAction.PRESS}
            />
          </HeaderCellContainer>
        ),
        cell: (ctx) => {
          const programs: string[] = ctx.getValue() ?? []
          if (programs.length === 0) {
            return <BodyText text='-' color={colors.textSecondary} />
          }
          return <BodyText text={programs.map(getProgramLabel).join(', ')} />
        },
      }),
    ]
  }, [colors.iconDefault, colors.textSecondary, formatDate, getProgramLabel, intl, isDaProvider])
}

export default useDocumentsColumns
